/**
 * Homepage narrative content — ICONIQ Impact layout pattern:
 * brand hero → our model (3 pillars) → initiatives (3 cards) →
 * proof stats → human story → engage.
 */

export type StatAccent = 'cyan' | 'coral' | 'violet';

export type HomepageStat =
  | {
      id: string;
      kind: 'text';
      valueLabel: string;
      description: string;
      source: string;
      accent: StatAccent;
    }
  | {
      id: string;
      kind: 'number';
      target: number;
      prefix?: string;
      suffix?: string;
      description: string;
      source: string;
      accent: StatAccent;
    }
  | {
      id: string;
      kind: 'timeline';
      startYear: string;
      startLabel: string;
      endYear: string;
      endLabel: string;
      source: string;
      accent: StatAccent;
    };

export type ProjectTile = {
  id: string;
  indexLabel: string;
  name: string;
  eyebrow: string;
  status: string;
  /** Short hook under the name. */
  descriptor: string;
  /** Designed body for cards. */
  body: string;
  bullets: string[];
  href: string;
  accent: StatAccent;
  imageSrc: string;
  imageAlt: string;
  ctaLabel: string;
};

export type Testimonial = {
  id: string;
  quote: string;
  name: string;
  role: string;
  storyHref?: string;
  storyLabel?: string;
};

export const heroContent = {
  initiative: 'Proudly supported by Tour de Cure',
  brand: 'NextOnVera',
  brandLines: ['NextOnVera', 'Project Brain Cancer'] as const,
  headline: 'NextOnVera',
  /** ICONIQ-style model line */
  modelTitle: 'A funding model built to move at the speed the science demands',
  pitch: 'The gap between a promising idea and fundable evidence.',
  subhead:
    'Brain cancer is not short of brilliant researchers or genuine breakthroughs waiting to happen. It is short of a funding model built to move at the speed the science demands — one willing to back the high-risk, high-potential work where real breakthroughs begin, before industry or competitive grants will touch it.',
  primaryCta: {
    label: 'Our model',
    href: '#our-model'
  },
  secondaryCta: {
    label: 'What we fund',
    href: '#what-we-fund'
  },
  tertiaryCta: {
    label: "Mark's story",
    href: '/my-story'
  },
  /**
   * Hero slideshow — Tour De Cure fact slides, 720×692, rotate every 6s.
   * Displayed with object-contain so the native ratio is never stretched.
   */
  heroSlides: [
    {
      src: 'generated-images/hero-slide-1.jpg',
      alt: 'Brain cancer kills more kids under 16 than any other disease.',
    },
    {
      src: 'generated-images/hero-slide-2.jpg',
      alt: 'It is the leading cause of cancer death for people under 40.',
    },
    {
      src: 'generated-images/hero-slide-3.jpg',
      alt: 'Five-year life expectancy for glioblastoma is 5%.',
    },
    {
      src: 'generated-images/hero-slide-4.jpg',
      alt: 'Median life expectancy for glioblastoma is 12 months.',
    },
    {
      src: 'generated-images/hero-slide-5.jpg',
      alt: 'Treatment has remained the same for 25 years.',
    },
    {
      src: 'generated-images/hero-slide-6.jpg',
      alt: 'Estimated in Australia, 2025: 1,639 GBM deaths and 2,098 new diagnoses.',
    },
    {
      src: 'generated-images/hero-slide-7.jpg',
      alt: '1,328 cancer research projects funded by Tour De Cure since inception in 2007.',
    },
    {
      src: 'generated-images/hero-slide-8.jpg',
      alt: '275 cancer breakthroughs funded by Tour De Cure.',
    },
  ],
} as const;

/**
 * "Our Model" — three equal pillars (ICONIQ Convene / Collaborate / Co-Fund pattern).
 */
export const purposeSection = {
  id: 'our-model',
  eyebrow: 'Our model',
  heading: 'Funding the science others can’t move fast enough to back',
  lead:
    'Traditional funding pathways are essential, but they are built for predictability — established teams, established protocols, established evidence. They were never designed to back the high-risk, high-potential, cross-disciplinary work where real breakthroughs begin. NextOnVera exists to fund exactly that: the ideas too early for NHMRC or MRFF, but too important to lose.',
  body: [] as string[],
  pillars: [
    {
      id: 'fund-the-gap',
      index: '01',
      title: 'Fund the gap',
      body: 'Back the research that exists between a promising idea and an established, fundable body of evidence — the translational stage where the most innovative work lives, and where it is most likely to die without early backing.'
    },
    {
      id: 'govern-with-discipline',
      index: '02',
      title: 'Govern with discipline',
      body: 'Every dollar is directed to research through Tour de Cure’s twenty years of grants discipline — an independent, competitive process open to the best ideas from any Australian institution, with full transparency on what is funded and what is found.'
    },
    {
      id: 'change-the-trajectory',
      index: '03',
      title: 'Change the trajectory',
      body: 'A future where a brain cancer diagnosis carries the same hope, options, and progress that patients with other cancers have come to expect — and where no promising discovery is lost simply because the system couldn’t move fast enough to back it.'
    }
  ]
} as const;

/** @deprecated alias kept for residual imports */
export const approachSection = purposeSection;

export const homepageStats: HomepageStat[] = [
  {
    id: 'child-disease-death',
    kind: 'text',
    valueLabel: '#1',
    description:
      'Cause of disease death in children under 16.',
    source: 'Tour De Cure briefing materials, 2026',
    accent: 'coral'
  },
  {
    id: 'under-40',
    kind: 'text',
    valueLabel: '#1',
    description:
      'Cancer killer of people under 40.',
    source: 'Tour De Cure briefing materials, 2026',
    accent: 'violet'
  },
  {
    id: 'gbm-survival-months',
    kind: 'text',
    valueLabel: '12 months',
    description:
      'Median survival for glioblastoma, the most aggressive common form.',
    source: 'Tour De Cure briefing materials, 2026',
    accent: 'cyan'
  },
  {
    id: 'treatment-gap',
    kind: 'number',
    target: 25,
    suffix: '+ years',
    description:
      'No change in standard treatment or survival outcomes for brain cancer.',
    source: 'Tour De Cure briefing materials, 2026',
    accent: 'coral'
  },
  {
    id: 'temozolomide-standard',
    kind: 'timeline',
    startYear: '1999',
    startLabel: 'Temozolomide first used.',
    endYear: '2026',
    endLabel: 'Still the standard treatment for GBM.',
    source: 'Tour De Cure briefing materials, 2026',
    accent: 'violet'
  }
];

export const fundingGapCallout =
  "Brain cancer is not short of ideas. It is short of a funding model built to move at the speed the science demands. Commercial and government funding overwhelmingly favours late-stage, low-risk trials — leaving the earliest and most innovative work without the backing to ever reach a patient.";

export type ComparisonStat = {
  label: string;
  value: string;
  isBrainCancer: boolean;
};

export type StallReason = {
  title: string;
  body: string;
};

/**
 * “The Reality of Brain Cancer” — facts section showing why progress has stalled.
 * Content drawn from the briefing mockup; comparison stats and structural barriers.
 */
export const brainFactsSection = {
  id: 'the-reality',
  eyebrow: 'The reality',
  heading: 'Why outcomes haven’t changed in 25+ years',
  lead:
    'Brain cancer is complex, deadly, and chronically underfunded. While other cancers have seen dramatic improvements, survival has barely moved in three decades.',
  comparisonHeading: 'How brain cancer compares',
  comparisonStats: [
    { label: 'Prostate cancer', value: '96%', isBrainCancer: false },
    { label: 'Breast cancer', value: '93%', isBrainCancer: false },
    { label: 'Brain cancer', value: '24%', isBrainCancer: true },
    { label: 'Glioblastoma', value: '6%', isBrainCancer: true }
  ] as ComparisonStat[],
  comparisonCaption:
    'Five-year survival rates. Brain cancer has barely moved while other major cancers transformed.',
  stalledHeading: 'Why progress has stalled',
  stallReasons: [
    {
      title: 'No national data platform',
      body: 'Brain tumour data is fragmented across institutions and countries, limiting learning at scale.'
    },
    {
      title: 'Limited diagnostics',
      body: 'Outside MRI imaging, there are few reliable tools. Surgery and biopsy are high-risk, last-resort options.'
    },
    {
      title: 'The blood-brain barrier',
      body: 'Most drugs can’t reach the tumour. Therapies that work elsewhere in the body fail in the brain — not because the science is wrong, but because delivery is fundamentally different.'
    },
    {
      title: 'Few clinical trials',
      body: 'Trials are limited, expensive, and hard to run. Very few exist globally.'
    },
    {
      title: 'Chronic underfunding',
      body: 'Roughly 90% of brain cancer research proposals go unfunded. Brain cancer is viewed as too hard, too risky, or lacking near-term ROI — despite devastating impact on families and communities.'
    }
  ] as StallReason[],
  barriersHeading: 'Structural barriers',
  barriers: [
    'No standardised environmental or genomic data',
    'Surgery and biopsy are high-risk procedures',
    'Very few viable treatment pathways',
    'Brain cancer remains a funding outlier',
    'ROI bias deters investment despite massive community impact'
  ] as readonly string[],
  conclusion:
    'When systems, funding, data, and incentives don’t align, survival rates don’t change. This is why the status quo has failed — and why a different model is required.'
} as const;

export const projectSection = {
  eyebrow: 'What we fund',
  heading: 'Current Initiatives',
  intro:
    'A focused portfolio of clinical intelligence, patient support technology, and orientation tools — designed first for brain cancer, built to travel further.'
} as const;

export const projectTiles: ProjectTile[] = [
  {
    id: 'nextonvera',
    indexLabel: '01',
    name: 'NextOnVera',
    eyebrow: 'Oncology intelligence',
    status: 'Test',
    descriptor: 'Clinical depth for every tumour board — including regional teams.',
    body: 'AI-powered oncology intelligence that structures a full molecular and clinical picture, then surfaces treatment, repurposing and trial options as evidence-traced reports for clinicians — never a black-box replacement.',
    bullets: [
      'Full molecular + clinical profile',
      'Treatment, repurposing and trial matching',
      'Structured reports for tumour boards'
    ],
    href: '/nextonvera',
    accent: 'cyan',
    imageSrc: 'generated-images/nextonvera-art.png',
    imageAlt: 'Abstract clinical intelligence neural map in teal light.',
    ctaLabel: 'Learn more'
  },
  {
    id: 'grace-ai',
    indexLabel: '02',
    name: 'Grace AI',
    eyebrow: 'Patient wellness companion',
    status: 'Test',
    descriptor: 'It does not answer questions — it asks them.',
    body: 'A talk-first companion that turns patient conversations into structured wellness signals for care teams, with hard clinical guardrails: never diagnose or advise, always escalate safety risk to a person.',
    bullets: [
      'Talk-first check-ins',
      'Trends surfaced to the care team',
      'Safety handoff, not chatbot advice'
    ],
    href: '/grace-ai',
    accent: 'coral',
    imageSrc: 'generated-images/grace-ai-art.png',
    imageAlt: 'Abstract wellbeing companion artwork with warm neural light.',
    ctaLabel: 'Learn more'
  },
  {
    id: 'cancer-navigator',
    indexLabel: '03',
    name: 'Cancer Navigator',
    eyebrow: 'Orientation after diagnosis',
    status: 'In development',
    descriptor: 'A clear path when the system is at its most overwhelming.',
    body: 'Guided support for newly diagnosed patients and carers — trusted information and practical next steps, co-shaped with consumer advocates.',
    bullets: [
      'Just-in-time trusted information',
      'Designed with patients and carers',
      'Less isolation after diagnosis'
    ],
    href: '/cancer-navigator',
    accent: 'violet',
    imageSrc: 'generated-images/cancer-navigator-art.png',
    imageAlt: 'Abstract patient pathway compass of soft medical light.',
    ctaLabel: 'Learn more'
  }
];

export const enableSection = {
  eyebrow: 'How to participate',
  heading: 'Give, commit, or advocate',
  items: [
    {
      id: 'give',
      title: 'Give',
      body: 'Donate securely via Tour De Cure — tax-deductible where eligible — and channel support into NextOnVera priorities.',
      href: '/donate',
      external: false,
      cta: 'Donate'
    },
    {
      id: 'fund',
      title: 'The Fund',
      body: 'Explore how individual donors, philanthropists, and corporates pool gifts into a single research grants pool — with multi-year commitments that give researchers certainty to plan bold work.',
      href: '/the-fund',
      external: false,
      cta: 'Explore the fund'
    },
    {
      id: 'register',
      title: 'Register interest',
      body: 'Individuals, corporates, family offices, clinicians and advocates — tell us how you want to join the work. Every donor is celebrated, at every level.',
      href: '/get-involved',
      external: false,
      cta: 'Get involved'
    }
  ]
} as const;

export const testimonials: Testimonial[] = [
  {
    id: 'mark-norris',
    quote:
      'Cancer is part of my story. It does not define me. Hope gives me the strength to face what comes next, and how I choose to face it defines who I am.',
    name: 'Mark Norris',
    role: 'Founder, NextOnVera',
    storyHref: '/my-story',
    storyLabel: "Read Mark's full story"
  }
];

export const fundTeaser = {
  eyebrow: 'Join us',
  heading: 'Put early capital behind better outcomes',
  body:
    'The Brain Cancer Fund backs NextOnVera and early-stage, high-risk research that falls outside traditional funding — the ideas too early for NHMRC or MRFF, but too important to lose. Every gift, from an individual donor to a multi-year corporate commitment, pools into the same research grants pool. We celebrate the generosity of every donor at every level.',
  vision:
    'A future where a brain cancer diagnosis carries the same hope, options, and progress that patients with other cancers have come to expect — and where no promising discovery is lost simply because the system couldn’t move fast enough to back it.',
  image: {
    src: 'generated-images/fund-coalition-band.png',
    alt: 'Abstract coalition of luminous pathways merging into hopeful light.'
  },
  ctas: [
    {
      id: 'give',
      label: 'Give via Tour De Cure',
      href: '/donate',
      variant: 'cta' as const
    },
    {
      id: 'partner',
      label: 'Explore The Fund',
      href: '/the-fund',
      variant: 'outline' as const
    },
    {
      id: 'advocate',
      label: 'Register interest',
      href: '/get-involved',
      variant: 'link' as const
    }
  ]
} as const;
